import { Badge, Button, Icon, IconButton, Image, Tabs } from "components";
import { DownloadAppDrawer, GdprModal, Loading } from "containers";
import QrCodeDrawer from "containers/QrCodeDrawer";
import { sortHashtags } from "helpers";
import { useAxios, useTheme, useToggle, useUserIsPro } from "hooks";
import { Layout } from "layouts";
import Error404 from "pages/Error404";
import {
  createContext,
  createElement,
  useEffect,
  useMemo,
  useState,
} from "react";
import Container from "react-indiana-drag-scroll";
import { useParams } from "react-router";
import { Fragment } from "react/jsx-runtime";
import { User } from "types";
import AboutMe from "./AboutMe";
import Files from "./Files";
import Links from "./Links";
import Magazine from "./Magazine";
import Shared from "./Shared";

type Context = {
  user: User.Profile;
  linksRoom: string;
  hasAvatarUrl: boolean;
  isBusinessProfile: boolean;
};
export const PersonDetailsContext = createContext({} as Context);

const normalizeTheme = (theme: string | null | undefined) => {
  const value = theme?.trim();
  if (!value) return null;
  if (/^#[0-9a-f]{6}$/i.test(value)) return value;
  if (/^[0-9a-f]{6}$/i.test(value)) return `#${value}`;
  return value;
};

export default function PersonDetails() {
  const [axios, loading] = useAxios();
  const { userId } = useParams();
  const [data, setData] = useState<User.Profile | null>(null);
  const [notFound, setNotFound] = useState(false);
  const [openDownloadApp, toggleDownloadApp] = useToggle(false);
  const [openQrCode, toggleQrCode] = useToggle(false);
  const [initIsPro] = useUserIsPro(data?.id ?? null);

  const isPro = initIsPro || !!data?.isPro || !!data?.canAccessMagazine;
  const profileTheme = normalizeTheme(data?.theme);
  const hasCustomProfileTheme = profileTheme?.toLowerCase() !== "#f5f8fa";
  const hasProfileTheme = !!profileTheme && (isPro || hasCustomProfileTheme);
  const isBusinessProfile =
    !!data?.isBusinessProfile || !!data?.IsBusinessProfile;
  const businessSlogan =
    data?.businessSlogan?.trim() || data?.BusinessSlogan?.trim() || "";
  const businessAvatarShape =
    data?.businessAvatarShape ?? data?.BusinessAvatarShape ?? "Circle";
  const isBusinessAvatarCircle =
    isBusinessProfile && businessAvatarShape !== "Square";

  const tabs = useMemo(() => {
    return [
      { name: "About", id: "about-me", component: AboutMe, active: true },
      { name: "Links", id: "links", component: Links, active: true },
      { name: "Files", id: "files", component: Files, active: true },
      {
        name: "Magazine",
        id: "magazine",
        component: Magazine,
        active: isPro,
      },
      { name: "Shared", id: "shared", component: Shared, active: true },
    ].filter((e) => e.active);
  }, [isPro]);

  const linksRoom = `https://${window.location.host}/${data?.urlKey}`;
  const title = data?.careerInfo?.title?.[0].value;
  const hasTitle = !!title;
  const hasLocation = !!data?.locationInfo;
  const avatarUrl = data?.avatar?.url;
  const hasAvatarUrl = !!avatarUrl;

  const getData = () => {
    const url = "/People";
    const config = { params: { key: userId } };
    setData(null);
    setNotFound(false);
    axios
      .get(url, config)
      .then(({ data }) => {
        // data?.urlKey
        setData(data);
      })
      .catch((err) => {
        if (err?.response?.status === 404) setNotFound(true);
      });
  };
  useEffect(getData, [userId]);
  useTheme(hasProfileTheme ? profileTheme : null);
  if (loading) return <Loading />;
  if (notFound || !data) return <Error404 />;
  return (
    <Fragment>
      <div className="absolute top-0 left-0 right-0 w-full h-[13.5rem] bg-theme z-[-1]"></div>
      <Layout>
        <Layout.Header
          divProps={{ className: "flex items-center justify-between gap-4" }}
        >
          <Button type="button" className="p-0" onClick={toggleDownloadApp}>
            <Icon name="Import" className="size-6" />{" "}
            <span className="font-semibold">Download App</span>
          </Button>
          <IconButton
            type="button"
            color="dark"
            className="-me-1.5"
            onClick={toggleQrCode}
          >
            <Icon name="Scanner" variant="Bold" />
          </IconButton>
        </Layout.Header>
        <Layout.Body className="px-0">
          <Tabs activeKey={tabs[0].id}>
            {hasAvatarUrl ? (
              <Image
                src={avatarUrl}
                alt={[data.firstName, data.lastName].join(" ")}
                className={[
                  "block size-[10.375rem] object-cover mx-auto mb-4 bg-white",
                  isBusinessAvatarCircle ? "rounded-full" : "rounded",
                ].join(" ")}
              />
            ) : (
              <div
                className={[
                  "flex justify-center items-center size-[10.375rem] mx-auto mb-4 bg-secondary-theme",
                  isBusinessAvatarCircle ? "rounded-full" : "rounded",
                ].join(" ")}
              >
                <Icon
                  name="User"
                  variant="Bold"
                  className="text-[#9A9EB1] size-[4.375rem]"
                />
              </div>
            )}
            <section className="w-full px-5">
              <h1 className="block w-full text-center text-xl text-[#181C32] font-semibold mb-1">
                {data.firstName} {data.lastName}
              </h1>
              {hasTitle && (
                <p className="text-sm text-[#9A9EB1] text-center font-medium mb-1">
                  {title}
                </p>
              )}
              {!!businessSlogan && (
                <p className="mx-auto mb-1 max-w-full px-[35px] text-center text-xs font-medium leading-4 text-[#3D3F4E] line-clamp-3">
                  {businessSlogan}
                </p>
              )}
              {hasLocation && (
                <p className="flex items-center justify-center gap-px text-xs text-primary text-center font-medium mb-2">
                  <Icon name="LocationTick" variant="Bold" className="size-4" />{" "}
                  {data.locationInfo?.city}, {data.locationInfo?.country}
                </p>
              )}
              <Container
                className="w-full flex py-2.5 mb-2.5 gap-1"
                hideScrollbars
              >
                {sortHashtags(data.hashTags)?.map((e) => (
                  <Badge key={e}>#{e}</Badge>
                ))}
              </Container>
              <Tabs.ButtonGroup
                className={[
                  "mb-5 h-10 overflow-hidden",
                  hasProfileTheme ? "bg-theme" : "bg-[#ECEFF2]",
                ].join(" ")}
              >
                {tabs.map((e) => (
                  <Tabs.Button key={e.id} eventKey={e.id}>
                    {e.name}
                  </Tabs.Button>
                ))}
              </Tabs.ButtonGroup>
            </section>
            <PersonDetailsContext.Provider
              value={{
                user: data,
                linksRoom,
                hasAvatarUrl,
                isBusinessProfile,
              }}
            >
              {tabs.map((e) => (
                <Tabs.Item key={e.id} eventKey={e.id}>
                  {createElement(e.component)}
                </Tabs.Item>
              ))}
              <DownloadAppDrawer
                isOpen={openDownloadApp}
                toggle={toggleDownloadApp}
              />
              <QrCodeDrawer isOpen={openQrCode} toggle={toggleQrCode} />
            </PersonDetailsContext.Provider>
          </Tabs>
        </Layout.Body>
        <Layout.Footer />
      </Layout>
      <GdprModal />
    </Fragment>
  );
}
